import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addSupport } from '../../../api/endpoints/support';
import { SUPPORT_TYPE_LABELS, type SupportType } from '../../../api/types/support';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';

const schema = z.object({
  subject: z.string().min(1, 'Konu gerekli'),
  supportType: z.enum(['TicketRefund', 'TicketCancel', 'TicketChange', 'Accounting', 'Other']),
  // Confirmed required by the real API even for non-ticket-specific types like "Diğer"
  // (curl: "ShoppingCart Id kontrol ediniz." when blank) — see api/types/support.ts.
  shopCartID: z.string().min(1, 'Sepet/Rezervasyon No (ShopCart ID) gerekli'),
  message: z.string().min(1, 'Mesaj gerekli'),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = { subject: '', supportType: 'Other', shopCartID: '', message: '' };

export function SupportFormDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => addSupport(values),
    onSuccess: () => {
      enqueueSnackbar('Destek talebi oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['support', 'list'] });
      reset(emptyValues);
      onClose();
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Yeni Destek Talebi</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Konu"
                error={!!errors.subject}
                helperText={errors.subject?.message}
                {...register('subject')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="supportType"
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    size="small"
                    label="Talep Tipi"
                    error={!!errors.supportType}
                    helperText={errors.supportType?.message}
                  >
                    {(Object.keys(SUPPORT_TYPE_LABELS) as SupportType[]).map((type) => (
                      <MenuItem key={type} value={type}>
                        {SUPPORT_TYPE_LABELS[type]}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Sepet / Rezervasyon No (ShopCart ID)"
                error={!!errors.shopCartID}
                helperText={errors.shopCartID?.message}
                {...register('shopCartID')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Mesaj"
                error={!!errors.message}
                helperText={errors.message?.message}
                {...register('message')}
              />
            </Grid>
          </Grid>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Gönderiliyor…' : 'Gönder'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
